import {
  VorgangProzessSchritt,
  VorgangProzessSchrittStatus,
} from '@core/domain';
import {
  Archive,
  CheckCheck,
  ChevronsLeftRightEllipsis,
  Clock,
  FileClock,
  FileInput,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TimelineElement } from '@/components/ui/timeline/timeline-types';
import type { VorgangBearbeitungLocationState } from '@/pages/VorgangBearbeitung';
import { VorgangDetail, VorgangDetailProtokoll } from '@/types';
import { getArchivierungBeschreibung } from './getArchivierungBeschreibung';
import { getSubStepItemForZwischenSpeicherung } from './getSubStepItemForZwischenSpeicherung';
import {
  STATUS_PROPERTY_MAP,
  VORGANG_TYP_TRANSLATION_MAP,
  VorgangDetailAbarbeitung,
  VorgangDetailSchritt,
} from './utils/common';
import { getPreviousPayloadForSchritt } from './utils/getPreviousPayloadForSchritt';

const TITLE_PROPERTY_MAP: Record<
  VorgangProzessSchritt,
  Pick<TimelineElement, 'title' | 'icon'>
> = {
  [VorgangProzessSchritt.TRANSFORMATION]: {
    title: 'Transformation',
    icon: <ChevronsLeftRightEllipsis />,
  },
  [VorgangProzessSchritt.WARTE_AUF_REAKTIVIERUNG_DES_DOKUMENTTYPS]: {
    title: 'Verarbeitung pausiert durch Pausierung des Dokumenttyps',
    icon: <Clock />,
  },
  [VorgangProzessSchritt.FACHLICHE_VALIDIERUNG]: {
    title: 'Fachliche Validierung',
    icon: <ShieldCheck />,
  },
  [VorgangProzessSchritt.ANTWORT_VON_DOKUMENTDIENSTLEISTER]: {
    title: 'Antwort von Streamserve',
    icon: <FileClock />,
  },
  [VorgangProzessSchritt.ARCHIVIERUNG]: {
    title: 'Archivierung',
    icon: <Archive />,
  },
  [VorgangProzessSchritt.WEITERLEITUNG_AN_DOKUMENTDIENSTLEISTER]: {
    title: 'Weiterleitung an Streamserve',
    icon: <FileInput />,
  },
  [VorgangProzessSchritt.EMAIL_VERSAND]: {
    title: 'Email Versand',
    icon: <Send />,
  },
  [VorgangProzessSchritt.BESTAETIGE_VERARBEITUNG_BEI_DOKUMENTDIENSTLEISTER]: {
    title: 'Versand bestätigt',
    icon: <CheckCheck />,
  },
};

const getDescription = (
  vorgang: VorgangDetail,
  item: VorgangDetailSchritt,
  archivierungEntkoppelnAktiv: boolean,
) => {
  const vorgangsTyp = vorgang.type;
  const appendInternalError = (description: string) => {
    if (item.status === VorgangProzessSchrittStatus.FEHLER && item.fehler) {
      return (
        <>
          <p className={'pb-2'}>{description}</p>
          <p>Fehlerbeschreibung: {item.fehler.message}</p>
          {item.fehler.internalError &&
            item.fehler.internalError !== item.fehler.message && (
              <p>Technische Ursache: {item.fehler.internalError}</p>
            )}
        </>
      );
    }

    return description;
  };

  switch (item.schritt as VorgangProzessSchritt) {
    case VorgangProzessSchritt.TRANSFORMATION:
      return appendInternalError(
        `Transformation von einem ${VORGANG_TYP_TRANSLATION_MAP[vorgangsTyp]}-Event in Dokument-Quelldaten`,
      );
    case VorgangProzessSchritt.WARTE_AUF_REAKTIVIERUNG_DES_DOKUMENTTYPS:
      return `Die Abarbeitung des Vorgangs wurde durch die Deaktivierung des Dokumenttyps bis auf Weiteres pausiert. Die Abarbeitung wird wieder aufgenommen, sobald der Dokumenttyp aktiviert wird.`;
    case VorgangProzessSchritt.FACHLICHE_VALIDIERUNG:
      return appendInternalError(`Überprüfung der Dokument-Quelldaten`);
    case VorgangProzessSchritt.ANTWORT_VON_DOKUMENTDIENSTLEISTER:
      if (!archivierungEntkoppelnAktiv) {
        return appendInternalError(
          'Die Dokument-Quelldaten werden von Streamserve verarbeitet - zurück kommt entweder eine fertige E-Mail, oder ein Dokument zum Archivieren.',
        );
      }
      return appendInternalError(
        (item.initialPayload as { archivierungAktiv?: boolean } | null)
          ?.archivierungAktiv === undefined
          ? 'Die Antwort von StreamServe ist eingegangen.'
          : `Die Antwort von StreamServe ist eingegangen. ${getArchivierungBeschreibung(item)}`,
      );
    case VorgangProzessSchritt.ARCHIVIERUNG:
      return appendInternalError(getArchivierungBeschreibung(item));
    case VorgangProzessSchritt.WEITERLEITUNG_AN_DOKUMENTDIENSTLEISTER:
      return appendInternalError(
        'Die Dokument-Quelldaten werden an Streamserve weitergeleitet.',
      );
    case VorgangProzessSchritt.EMAIL_VERSAND:
      if (!archivierungEntkoppelnAktiv) {
        return appendInternalError('Die E-Mail wird an den Kunden versendet.');
      }
      return appendInternalError(
        item.status === VorgangProzessSchrittStatus.ERLEDIGT
          ? 'E-Mail erfolgreich an den Versanddienstleister übergeben.'
          : 'Die E-Mail wird an den Versanddienstleister übergeben.',
      );
    case VorgangProzessSchritt.BESTAETIGE_VERARBEITUNG_BEI_DOKUMENTDIENSTLEISTER:
      if (!archivierungEntkoppelnAktiv) {
        return appendInternalError(
          'Alle Prozessschritte sind erfolgreich abgeschlossen. Die Dokumente auf dem SFTP-Server werden aufgeräumt.',
        );
      }
      return appendInternalError(
        item.status === VorgangProzessSchrittStatus.ERLEDIGT
          ? 'Versandprozess abgeschlossen. Eine tatsächliche Zustellung wird hier nicht bestätigt.'
          : 'Die Verarbeitung bei StreamServe wird bestätigt und die Dateien werden aufgeräumt.',
      );
    default:
      return appendInternalError('Unbekannt');
  }
};

export const getTimelineItemForAbarbeitungsSchritt = ({
  vorgang,
  abarbeitung,
  schritt,
  zwischenSpeicherungen,
  navigate,
  locationState,
  urlParams,
  archivierungEntkoppelnAktiv,
  initiator = 'system',
}: {
  vorgang: VorgangDetail;
  abarbeitung: VorgangDetailAbarbeitung;
  schritt: VorgangDetailSchritt;
  zwischenSpeicherungen: VorgangDetailProtokoll;
  navigate: ReturnType<typeof useNavigate>;
  locationState: VorgangBearbeitungLocationState | null;
  urlParams: {
    abarbeitungId?: string;
    schrittId?: string;
  };
  archivierungEntkoppelnAktiv: boolean;
  initiator?: string;
}): Omit<TimelineElement, 'id'> => {
  const { abarbeitungId, schrittId } = urlParams;
  const onClickUrl = `/vorgaenge/${vorgang.id}/abarbeitungen/${abarbeitung.id}/schritte/${schritt.id}`;

  const previousPayload = getPreviousPayloadForSchritt({
    schritt,
    abarbeitungId: abarbeitung.id,
    abarbeitungen: vorgang.abarbeitungen,
    protokoll: zwischenSpeicherungen,
  });

  const isCurrentStepAndFailed =
    schritt.status === VorgangProzessSchrittStatus.FEHLER &&
    abarbeitungId === vorgang.letzteAbarbeitungId &&
    schrittId === schritt.id;
  const showHighlight = isCurrentStepAndFailed || previousPayload !== null;

  const description = getDescription(
    vorgang,
    schritt,
    archivierungEntkoppelnAktiv,
  );

  return {
    date: new Date(schritt.startedAt ?? schritt.createdAt),
    description,
    showHighlight,
    selected: abarbeitungId === abarbeitung.id && schrittId === schritt.id,
    initiator,
    subSteps: zwischenSpeicherungen
      .reverse()
      .map(getSubStepItemForZwischenSpeicherung),
    ...STATUS_PROPERTY_MAP[schritt.status],
    ...TITLE_PROPERTY_MAP[schritt.schritt],
    ...((schritt.schritt as VorgangProzessSchritt) ===
      VorgangProzessSchritt.ARCHIVIERUNG &&
      schritt.successPayload?.archivierungErgebnis === 'UEBERSPRUNGEN' && {
        title: 'Archivierung übersprungen',
      }),
    ...((schritt.schritt as VorgangProzessSchritt) ===
      VorgangProzessSchritt.ARCHIVIERUNG &&
      schritt.successPayload?.archivierungErgebnis === 'WIEDERHOLUNG' && {
        title: 'Archivierung erneut versucht',
      }),
    ...(archivierungEntkoppelnAktiv &&
      schritt.schritt ===
        VorgangProzessSchritt.BESTAETIGE_VERARBEITUNG_BEI_DOKUMENTDIENSTLEISTER &&
      schritt.status === VorgangProzessSchrittStatus.ERLEDIGT && {
        title: 'Versandprozess abgeschlossen',
      }),
    onClick: () => {
      // Herkunft bewahren, damit der Rücksprung auch nach dem Wechsel
      // zwischen Schritten zur Quellseite zurückführt.
      navigate(onClickUrl, { state: locationState });
    },
  } satisfies Omit<TimelineElement, 'id'>;
};
